import { Repetition, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import React, { useCallback, useEffect, useState } from "react";
import TextField from "../TextField";
import { WORKING_DAYS_PER_WEEK } from "../../utils";
import { useI18n } from "../../hooks/i18n-hooks";

export type TimePerWeekClassicComponentProps = {
    workingTime: WorkingTimeClassicType;
    onChange: (v: { workingTime: WorkingTimeClassicType }) => void;
    hoursPerWeek: number;
    error?: string;
    disabled?: boolean;
};

const convertUnitCount = (
    unitCount: number | undefined,
    from: Unit,
    to: Unit,
    hoursPerWeek: number,
): number | undefined => {
    if (unitCount == undefined) {
        return undefined;
    }
    if (from === to) {
        return unitCount;
    }
    const hoursPerDay = hoursPerWeek / WORKING_DAYS_PER_WEEK;
    if (from === Unit.Hours && to === Unit.Days) {
        return Math.round(unitCount / hoursPerDay);
    }
    if (from === Unit.Days && to === Unit.Hours) {
        return Math.round(unitCount * hoursPerDay);
    }
    throw new Error("Invalid conversion");
};

const MonthlyTimePerWeekClassicComponent: React.FC<TimePerWeekClassicComponentProps> = ({
    workingTime,
    onChange,
    disabled = false,
    hoursPerWeek,
}) => {
    /* Component is using minutes as value */
    const i18n = useI18n();
    const unit = Unit.Hours;
    const repetition = Repetition.Week;
    const [textValue, setTextValue] = useState(workingTime.unitCount?.toString() || "");

    useEffect(() => {
        if (workingTime.unit !== unit || workingTime.repetition !== repetition) {
            const converted = convertUnitCount(
                workingTime.unitCount || undefined,
                workingTime.unit, // Unit.Days
                unit,
                hoursPerWeek,
            );
            onChange({ workingTime: { repetition, unit, unitCount: converted } });
        }
    }, [workingTime, unit, repetition, hoursPerWeek, onChange]);

    useEffect(() => {
        if (workingTime.unitCount !== undefined) {
            setTextValue(workingTime.unitCount?.toString() || "");
        }
    }, [workingTime.unitCount]);

    const onBlur = useCallback(() => {
        const valueAsNumber = Number(textValue.replace(",", "."));
        let changedValue: WorkingTimeClassicType | null;
        if (isNaN(valueAsNumber) || valueAsNumber <= 0 || valueAsNumber > 24 * 7) {
            changedValue = { repetition, unit, unitCount: undefined };
        } else {
            changedValue = { repetition, unit, unitCount: valueAsNumber };
        }
        onChange({ workingTime: changedValue });
    }, [onChange, repetition, textValue, unit]);
    return (
        <TextField
            label={i18n.t("utils.working_time.hours_label")}
            style={{ flexShrink: 1, flexGrow: 0, width: "auto" }}
            selectTextOnFocus
            inputStyle={{
                textAlign: "right",
                color: workingTime?.unitCount === undefined ? "red" : undefined,
            }}
            disabled={disabled}
            value={textValue}
            isDecimalNumber
            maxValue={hoursPerWeek}
            onChangeText={setTextValue}
            onBlur={onBlur}
            keyboardType="decimal-pad"
            returnKeyType="done"
        />
    );
};

export default MonthlyTimePerWeekClassicComponent;
